import { type ReactElement, type ReactNode } from 'react';
import { isElement } from 'react-is';

import { _canReceiveDefaultTextFormat } from '@dynatrace/strato-components/core';
import { Text } from '@dynatrace/strato-components/typography';

import { useTimeframeSelectorContext } from './hooks/useTimeframeSelectorContext.js';

/**
 * @public
 */
export type TimeframeSelectorDisplayValueProps = {
  children?:
    | ReactNode
    | ((customTriggerProps: { displayValue: ReactNode }) => ReactElement);
};

/**
 * Typeguard function that will check if a Node is a TimeframeSelector.DisplayValue
 * @internal
 */
export function _isTimeframeSelectorDisplayValueElement(
  child: ReactNode,
): child is ReactElement<TimeframeSelectorDisplayValueProps> {
  return isElement(child) && child.type === DisplayValue;
}

/**
 * `TimeframeSelector.DisplayValue` defines a custom display value within the Trigger. A TimeframeSelector.DisplayValue component
 * is only valid within a TimeframeSelector.Trigger.
 * @public
 */
export const DisplayValue = (props: TimeframeSelectorDisplayValueProps) => {
  const { children } = props;

  const { displayValue } = useTimeframeSelectorContext();

  if (!children) {
    return null;
  }

  if (typeof children === 'function') {
    return children({ displayValue });
  }

  if (_canReceiveDefaultTextFormat(children)) {
    return <Text as="p">{children}</Text>;
  }

  if (isElement(children)) {
    return children;
  }

  return null;
};

DisplayValue.displayName = 'TimeframeSelector.DisplayValue';
